import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

interface PlanLimitDialogProps {
  open: boolean;
  onClose: () => void;
  limitType: "user" | "editor" | "proposition" | "segment";
  currentPlan: string;
  currentCount?: number;
  limit?: number;
  onUpgrade?: () => void;
}

const planFeatures = {
  starter: {
    name: "Starter",
    price: "£29.99/month",
    description: "(includes 1 editor) + £14.99 per additional editor",
    features: [
      "Up to 5 team members",
      "Up to 5 editors with full access",
      "3 value propositions",
      "3 market segments",
      "AI-powered research assistant",
      "SAM calculator with AI",
      "Basic analytics",
    ],
  },
  professional: {
    name: "Professional",
    price: "£49.99/month",
    description: "(includes 1 editor) + £9.99 per additional editor",
    features: [
      "Unlimited team members",
      "Unlimited editors",
      "Unlimited value propositions",
      "Unlimited market segments",
      "AI-powered research assistant",
      "SAM calculator with AI",
      "Advanced analytics & reporting",
      "Priority support",
      "API access (coming soon)",
    ],
  },
};

export default function PlanLimitDialog({
  open,
  onClose,
  limitType,
  currentPlan,
  currentCount,
  limit,
  onUpgrade,
}: PlanLimitDialogProps) {
  const getTitle = () => {
    switch (limitType) {
      case "user":
        return "Team Size Limit Reached";
      case "editor":
        return "Editor Limit Reached";
      case "proposition":
        return "Value Proposition Limit Reached";
      case "segment":
        return "Market Segment Limit Reached";
      default:
        return "Plan Limit Reached";
    }
  };

  const getMessage = () => {
    const limitName = limitType === "user" ? "team members" : `${limitType}s`;
    if (currentCount !== undefined && limit !== undefined) {
      return `Your ${currentPlan} plan allows up to ${limit} ${limitName}. You currently have ${currentCount}.`;
    }
    return `You've reached the ${limitName} limit for your ${currentPlan} plan.`;
  };

  const recommendedPlan = currentPlan === "free" ? "starter" : "professional";
  const planInfo = planFeatures[recommendedPlan];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RocketLaunchIcon color="primary" />
          <Typography variant="h5" component="span">
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" className="mb-4">
          {getMessage()}
        </Alert>

        <Box mt={3}>
          <Typography
            variant="h6"
            gutterBottom
            className="flex items-center gap-2"
          >
            <AutoAwesomeIcon color="primary" fontSize="small" />
            Upgrade to {planInfo.name} Plan
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {planInfo.price} {planInfo.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Everything you need to scale your strategy
          </Typography>

          <List dense className="mt-2">
            {planInfo.features.map((feature, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon className="min-w-0 mr-2">
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {currentPlan === "free" && (
          <Alert severity="info" className="mt-4">
            <Typography variant="body2">
              <strong>Did you know?</strong> Free plan users still have full
              access to core features like creating propositions, segments, and
              viewing analytics. Only AI-powered features and team size are
              limited.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
        <Button
          onClick={() => {
            if (onUpgrade) {
              onUpgrade();
            } else {
              // Navigate to billing page
              window.location.href = "/organization?tab=billing";
            }
            onClose();
          }}
          variant="contained"
          color="primary"
          startIcon={<RocketLaunchIcon />}
        >
          Upgrade to {planInfo.name}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
